import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import i18n from '@/i18n';
import {
  getI18nBootstrap,
  getI18nTranslations,
  resolveGeo,
  updateUserLanguage,
  unflattenTranslations,
  getAuthMe,
} from '@/api';

interface LanguageContextType {
  language: string;
  setLanguage: (lang: string) => void | Promise<void>;
  isLoading: boolean;
  countryCode: string | null;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

const STORAGE_KEY = 'bazaarak_language';
const DETECTED_KEY = 'bazaarak_language_detected';
const USER_CHOICE_KEY = 'bazaarak_language_user_choice';
const BUNDLE_CACHE_PREFIX = 'bazaarak_i18n_bundle_';

const browserLangMap: Record<string, string> = {
  fa: 'fa',
  en: 'en',
  de: 'de',
  tr: 'tr',
  ar: 'ar',
};

function detectFromBrowser(): string {
  const navLang = navigator.language || (navigator as any).userLanguage || 'en';
  const prefix = navLang.split('-')[0].toLowerCase();
  return browserLangMap[prefix] || 'en';
}

function applyDomDirection(lang: string) {
  document.documentElement.lang = lang;
  document.documentElement.dir = lang === 'fa' || lang === 'ar' ? 'rtl' : 'ltr';
}

async function hydrateBundle(lang: string) {
  // Prefer cached last-good bundle for offline/error resilience
  try {
    const cached = localStorage.getItem(BUNDLE_CACHE_PREFIX + lang);
    if (cached) {
      const flat = JSON.parse(cached) as Record<string, string>;
      i18n.addResourceBundle(lang, 'translation', unflattenTranslations(flat), true, true);
    }
  } catch {
    /* ignore */
  }

  const bootstrap = await getI18nBootstrap(lang);
  const flat = bootstrap?.translations || (await getI18nTranslations(lang));
  if (flat && Object.keys(flat).length > 0) {
    i18n.addResourceBundle(lang, 'translation', unflattenTranslations(flat), true, true);
    try {
      localStorage.setItem(BUNDLE_CACHE_PREFIX + lang, JSON.stringify(flat));
    } catch {
      /* ignore quota */
    }
  }
}

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const [language, setLangState] = useState(() => localStorage.getItem(STORAGE_KEY) || '');
  const [isLoading, setIsLoading] = useState(true);
  const [countryCode, setCountryCode] = useState<string | null>(null);

  const setLanguage = useCallback(async (lang: string) => {
    setLangState(lang);
    localStorage.setItem(STORAGE_KEY, lang);
    localStorage.setItem(USER_CHOICE_KEY, 'true');
    // Keep i18next detector key in sync
    localStorage.setItem('i18nextLng', lang);
    applyDomDirection(lang);
    await hydrateBundle(lang);
    await i18n.changeLanguage(lang);

    if (typeof localStorage !== 'undefined' && localStorage.getItem('token')) {
      try {
        await updateUserLanguage(lang);
      } catch {
        /* guest or network — ignore */
      }
    }
  }, []);

  useEffect(() => {
    let mounted = true;

    async function init() {
      const saved = localStorage.getItem(STORAGE_KEY);
      const userChose = localStorage.getItem(USER_CHOICE_KEY) === 'true';
      let resolved = saved || '';
      let country: string | null = null;

      // Authenticated users: prefer saved UI choice; otherwise profile language once
      if (!resolved && typeof localStorage !== 'undefined' && localStorage.getItem('token')) {
        try {
          const me = await getAuthMe();
          const profileLang = (me?.language || '').toString().toLowerCase();
          if (profileLang && browserLangMap[profileLang]) {
            resolved = profileLang;
            localStorage.setItem(STORAGE_KEY, resolved);
            localStorage.setItem('i18nextLng', resolved);
          }
        } catch {
          /* ignore */
        }
      } else if (userChose && saved) {
        resolved = saved;
      }

      if (!resolved) {
        const geo = await resolveGeo();
        country = geo?.countryCode || null;
        if (geo?.suggestedLanguage) {
          resolved = geo.suggestedLanguage;
        } else {
          resolved = detectFromBrowser();
        }
        localStorage.setItem(DETECTED_KEY, 'true');
        localStorage.setItem(STORAGE_KEY, resolved);
        localStorage.setItem('i18nextLng', resolved);
      } else if (!localStorage.getItem(DETECTED_KEY)) {
        // Soft geo for recommendations even when language already chosen
        const geo = await resolveGeo();
        country = geo?.countryCode || null;
      } else {
        try {
          const geo = await resolveGeo();
          country = geo?.countryCode || null;
        } catch {
          /* ignore */
        }
      }

      if (!mounted) return;

      setCountryCode(country);
      setLangState(resolved);
      applyDomDirection(resolved);
      await hydrateBundle(resolved);
      await i18n.changeLanguage(resolved);
      if (mounted) setIsLoading(false);
    }

    init();
    return () => {
      mounted = false;
    };
  }, []);

  const value = { language, setLanguage, isLoading, countryCode };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useLanguage must be used within LanguageProvider');
  return ctx;
}
